import { keepPreviousData, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import type { SupplyClass } from '@wodi/domain';
import {
  Button,
  Cell,
  Column,
  LoadingBlock,
  Notice,
  Row,
  SegmentedControl,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextField,
  useShortcut,
} from '@wodi/ui';
import { useDeferredValue, useRef, useState } from 'react';

import { ApiError } from '../api/client.ts';
import { pharmacyKeys, searchItemSettings } from '../api/pharmacy.ts';
import { can, sessionQuery } from '../api/session.ts';
import { screen, TableScroll } from '../layout/screen.tsx';
import { useOnline } from '../hooks/use-online.ts';
import pharmacy from '../pharmacy/pharmacy.module.css';
import { SUPPLY_CLASS_LABELS } from '../pharmacy/supply-class.ts';
import { SettingsPage } from './settings-nav.tsx';

// Settings > Pharmacy items (docs/plans/phase1-dispensing.md §5.3; ADR 0014): every stock
// item with its supply class (prescription only or over the counter) and barcodes. Opening
// one changes them. A scanned barcode finds its item; Enter opens a single match.

/** The most the list shows at once; a narrower search finds the rest. */
const LIST_LIMIT = 100;

const ALL = 'all';
const FILTERS = [
  { id: ALL, label: 'All' },
  { id: 'prescription_only', label: SUPPLY_CLASS_LABELS.prescription_only },
  { id: 'over_the_counter', label: SUPPLY_CLASS_LABELS.over_the_counter },
] as const;

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

export function PharmacyItemsSettingsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const allowed = session !== null && can(session, 'catalogue.read');
  const online = useOnline();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const query = useDeferredValue(text.trim());
  const [filter, setFilter] = useState<string>(ALL);
  const searchRef = useRef<HTMLDivElement>(null);
  useShortcut(
    '/',
    () => {
      searchRef.current?.querySelector('input')?.focus();
    },
    { description: 'Find a pharmacy item' },
  );

  const body = {
    query,
    limit: LIST_LIMIT,
    ...(filter === ALL ? {} : { supplyClass: filter as SupplyClass }),
  };
  const items = useQuery({
    queryKey: pharmacyKeys.itemSettings(body),
    queryFn: () => searchItemSettings(body),
    enabled: allowed,
    placeholderData: keepPreviousData,
  });

  if (!allowed) {
    return (
      <SettingsPage title="Pharmacy items" panel>
        <Notice tone="info">
          You don't have permission to see the catalogue. Ask an administrator if you need it.
        </Notice>
      </SettingsPage>
    );
  }

  const rows = items.data ?? [];
  const searched = query !== '' || filter !== ALL;
  const current = query === text.trim() && !items.isPlaceholderData;
  return (
    <SettingsPage title="Pharmacy items" panel>
      {online ? null : (
        <Notice tone="info">
          You're offline. The list is as last loaded; changing an item needs a connection to Wodi.
        </Notice>
      )}
      <form
        className={screen.filters}
        aria-label="Find pharmacy items"
        onSubmit={(event) => {
          event.preventDefault();
          const [only] = rows;
          if (current && rows.length === 1 && only !== undefined)
            void navigate({
              to: '/settings/pharmacy-items/$itemId',
              params: { itemId: only.serviceItemId },
            });
        }}
      >
        <div ref={searchRef}>
          <TextField
            label="Find an item"
            description="Name, code or a scanned barcode. Press / to jump here."
            value={text}
            onChange={setText}
          />
        </div>
        <div className={screen.filtersAction}>
          <SegmentedControl
            aria-label="Supply class"
            options={FILTERS}
            value={filter}
            onChange={setFilter}
          />
        </div>
      </form>
      {items.isPending ? <LoadingBlock label="Loading pharmacy items" rows={6} /> : null}
      {items.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void items.refetch()}>
              Try again
            </Button>
          }
        >
          {message(items.error, "Pharmacy items couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {items.isSuccess ? (
        <TableScroll>
          <Table flat aria-label="Pharmacy items">
            <TableHeader>
              <Column>Code</Column>
              <Column isRowHeader>Name</Column>
              <Column>Supply class</Column>
              <Column numeric>Barcodes</Column>
              <Column>Status</Column>
            </TableHeader>
            <TableBody
              items={rows}
              renderEmptyState={() =>
                searched
                  ? 'Nothing matches. Check the spelling, search by code, or scan the barcode again.'
                  : 'No stock items yet. Set up stock details for medicines under Stores first.'
              }
            >
              {(item) => (
                <Row id={item.serviceItemId}>
                  <Cell>
                    <span className="wodi-id">{item.code}</span>
                  </Cell>
                  <Cell>
                    <Link
                      to="/settings/pharmacy-items/$itemId"
                      params={{ itemId: item.serviceItemId }}
                    >
                      {item.name}
                    </Link>
                  </Cell>
                  <Cell>
                    <SupplyClassText
                      supplyClass={item.supplyClass}
                      isControlled={item.isControlled}
                    />
                  </Cell>
                  <Cell numeric>
                    <span className="wodi-num">{item.barcodes.length}</span>
                  </Cell>
                  <Cell>
                    <Tag state={item.status === 'active' ? 'active' : 'neutral'}>
                      {item.status === 'active' ? 'In use' : 'Not in use'}
                    </Tag>
                  </Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      ) : null}
      {rows.length >= LIST_LIMIT ? (
        <p className={screen.hint}>
          Showing the first {LIST_LIMIT}. Type part of a name or a code to find others.
        </p>
      ) : null}
    </SettingsPage>
  );
}

/** The supply class in words, with a "Controlled" tag beside it when the item is. */
export function SupplyClassText({
  supplyClass,
  isControlled,
}: {
  supplyClass: SupplyClass;
  isControlled: boolean;
}) {
  return (
    <span className={pharmacy.actions}>
      {SUPPLY_CLASS_LABELS[supplyClass]}
      {isControlled ? <Tag state="pending">Controlled</Tag> : null}
    </span>
  );
}
